import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Add from '@mui/icons-material/Add';
import ArrowBack from '@mui/icons-material/ArrowBack';
import PageHeader from '../../../Components/PageHeader';
import TablePeo from '../../../Components/TablePeo';
import { useApiGet } from '../../../utils/httpClientTs';
import { listOnlineItems } from '../../../endpoints/chapterSupplies';
import type { OnlineItemResponse } from '../../../types/chapterSupplies';

const headCells = [
  { id: 'item_name', firstCell: true, label: 'Item name', width: '20%' },
  { id: 'item_type', label: 'Type', width: '10%' },
  { id: 'price', label: 'Price', width: '10%' },
  { id: 'is_featured', label: 'Featured', width: '8%' },
  { id: 'is_active', label: 'Active', width: '8%' },
  {
    id: 'tags',
    label: 'Tags',
    width: '14%',
    format: (v: unknown) =>
      v && String(v).length > 30
        ? `${String(v).slice(0, 30)}…`
        : (v ? String(v) : '') || '',
  },
  { id: 'sort_order', label: 'Sort', width: '8%' },
  {
    id: 'created_at',
    label: 'Created',
    width: '22%',
    format: (value: unknown) =>
      value
        ? new Date(value as string).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })
        : '',
  },
];

export default function OnlineItemList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState('created_at');
  const [tagsSearch, setTagsSearch] = useState('');

  const limit = rowsPerPage;
  const offset = page * rowsPerPage;

  const { data, isFetching } = useApiGet(
    ['chapter-supplies-online-items', limit, offset, tagsSearch],
    () => listOnlineItems({ limit, offset, tags: tagsSearch || undefined }),
    { enabled: true, retry: 1 }
  );

  const onlineItems = (data?.online_items ?? []).map(
    (o: OnlineItemResponse) => ({
      ...o,
      item_name: o.name ?? o.item_name ?? '—',
    })
  );
  const handleRowClick = (_event: React.MouseEvent, rowId: string | number) => {
    navigate(`/chapter-supplies-management/online-items/${rowId}`);
  };

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12}>
        <PageHeader
          title="Online Items"
          leftDom={
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/chapter-supplies-management')}
            >
              Back
            </Button>
          }
          rightDom={
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() =>
                navigate('/chapter-supplies-management/online-items/create')
              }
            >
              Add online item
            </Button>
          }
          subtitle={undefined}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} sx={{ mt: 1 }}>
        <TextField
          fullWidth
          size="small"
          label="Search by tags"
          placeholder="Comma-separated"
          value={tagsSearch}
          onChange={(e) => setTagsSearch(e.target.value)}
        />
      </Grid>
      <Grid item xs={12}>
        <TablePeo
          data={onlineItems}
          isFetching={isFetching}
          headCells={headCells}
          handleRowClick={handleRowClick}
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          order={order}
          setOrder={setOrder}
          orderBy={orderBy}
          setOrderBy={setOrderBy}
          emptyMessage="No online items yet. Create one to get started."
        />
      </Grid>
    </Grid>
  );
}
